import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Globe, Linkedin } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import toast from 'react-hot-toast';
import api from '../utils/axios';
import company from '../config/company';
import buildOrgReport, { aggregateByDesignation } from '../components/orgchart/orgReport';
import { groupChildrenByDepartment } from '../components/orgchart/orgTree';
import { colorsForDepartment } from '../components/orgchart/departmentColors';
import logo from '../assets/logo.svg';
import '../components/orgchart/orgchart-export.css';

// How many DISTINCT designations the mini chart draws under one leader
// before folding the rest into "+N more roles" - names are already gone at
// this level (Task 8 collapses a leader's reports into "Designation xN"
// chips), so the old per-PERSON cap (MAX_CHART_REPORTS/"+N more") no longer
// applies: aggregation already compresses however many people share a
// title, this only bounds how many distinct title rows one leader's block
// can grow to. The detailed streams/role-count table further down the
// document is unaffected.
const MAX_CHART_DESIGNATIONS = 10;

// "designation" -> a stable, URL/testid-safe slug ("Software Engineer" ->
// "software-engineer"). Used only to build the per-role-line testid below;
// never rendered.
const slugify = (text) => (text || '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'role';

// Groups a leader's FULL direct-report list by department (Task 7's
// grouping, unchanged), then aggregates each group's reports into
// "Designation xN" counts (`aggregateByDesignation`, Task 8) - names are
// gone at this level, so aggregation runs over every report, not a
// MAX_CHART_REPORTS-capped slice; a leader with 7 Software Engineers now
// renders "Software Engineer x7", not seven names or a person-count cap.
// The distinct-designation cap (MAX_CHART_DESIGNATIONS) applies across the
// WHOLE leader, in the same department-then-designation order the chips
// render in, so the note it produces ("+N more roles") always matches
// exactly what got cut off, wherever that landed.
const buildLeaderRoleGroups = (reports, maxDesignations = MAX_CHART_DESIGNATIONS) => {
  const departmentGroups = groupChildrenByDepartment(reports);
  const aggregated = departmentGroups.map((group) => ({
    ...group,
    roles: aggregateByDesignation(group.children),
  }));
  const totalDesignations = aggregated.reduce((sum, group) => sum + group.roles.length, 0);

  let remaining = maxDesignations;
  const groups = [];
  aggregated.forEach((group) => {
    if (remaining <= 0) return;
    const roles = group.roles.slice(0, remaining);
    remaining -= roles.length;
    if (roles.length > 0) groups.push({ ...group, roles });
  });

  return { groups, extraRoles: Math.max(0, totalDesignations - maxDesignations) };
};

// Same "first letter of first + last name, uppercased" rule as OrgNode.js's
// avatar - kept as a small local pure helper (not imported) because
// OrgNode's version is a private detail of that card component, not a
// shared export; duplicating three lines here is simpler than widening
// OrgNode's public surface for it.
const initialsOf = (firstName, lastName) => {
  const first = (firstName || '').trim()[0] || '';
  const last = (lastName || '').trim()[0] || '';
  return `${first}${last}`.toUpperCase();
};

// "Position ×N › Position ×N" - the exact glyphs the reference document
// uses (multiplication sign, single right angle quote), not "x" and ">".
const formatRoleCounts = (roleCounts) => roleCounts
  .map((rc) => `${rc.position} ×${rc.count}`)
  .join(' › ');

// "21 Sept 2026" - built from the SAME Date via one locale-aware call
// (day/month/year all read off the same `Date` instance's local time),
// rather than parsing as UTC and reading fields back out with local
// getters, which can disagree by a day right around midnight.
const formatGeneratedAt = (iso) => new Date(iso)
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// "1 employee has ... and is not shown" vs "N employees have ... and are
// not shown" - the closing note's singular/plural agreement.
const formatUnassignedNote = (count) => {
  const isOne = count === 1;
  return `${count} employee${isOne ? '' : 's'} ${isOne ? 'has' : 'have'} no reporting line recorded `
    + `and ${isOne ? 'is' : 'are'} not shown in the sections above.`;
};

// Same "has a department" predicate OrgNode's own card pill and
// groupChildrenByDepartment already use - lets a report group's normalized
// department name be compared directly against the leader's own.
const normalizedDeptName = (name) => name || 'No department';

// The mini chart's per-department caption (Task 7) - same deterministic
// department->colour mapping the interactive tree's branch labels and
// OrgNode's own pill use (`departmentColors.js`), scaled down for this
// compact print document rather than reusing the tree's larger chip.
const DeptCaption = ({ leaderId, group }) => {
  const colors = group.department_id != null ? colorsForDepartment(group.department_id) : null;
  return (
    <p
      className="export-chart__report-group-label"
      data-testid={`org-export-chart-dept-${leaderId}-${group.key}`}
    >
      <span
        className={twMerge(
          'inline-block whitespace-nowrap rounded-full px-1.5 py-px text-[7px] font-bold uppercase tracking-wide',
          colors ? colors.pill : 'bg-gray-100 text-gray-500'
        )}
      >
        {group.department_name}
      </span>
    </p>
  );
};

const MiniPerson = ({ person, size = 'md' }) => (
  <div className={`export-chart__person export-chart__person--${size}`}>
    <span className="export-chart__avatar">{initialsOf(person.first_name, person.last_name)}</span>
    <span className="export-chart__person-text">
      <span className="export-chart__person-name">{person.first_name} {person.last_name}</span>
      <span className="export-chart__person-position">{person.position}</span>
    </span>
  </div>
);

// One "Designation ×N" line in the mini chart's report roll-up (Task 8) -
// the exact glyph `formatRoleCounts` already uses for the section stream
// tables further down this same document, so the two roll-ups read as one
// consistent vocabulary rather than two different notations for the same
// idea.
const RoleCountLine = ({ leaderId, deptKey, role }) => (
  <p
    className="export-chart__role-count"
    data-testid={`org-export-chart-role-${leaderId}-${deptKey}-${slugify(role.position)}`}
  >
    {role.position} ×{role.count}
  </p>
);

/**
 * OrgChartExport - printable "Organization Structure" reference document,
 * built client-side from the same `GET /api/org-chart` payload as
 * `OrgChart.js`. `full` mode only: a scoped caller is bounced back to the
 * interactive chart before any company-wide data renders.
 *
 * Rendered outside `Layout` (see the route in `App.js`), so there is no app
 * chrome to hide from the printed page in the first place. The one
 * non-printing element on this page is the action bar, hidden via the
 * `.no-print` class in `orgchart-export.css`.
 */
const OrgChartExport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);
  const [nodes, setNodes] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/org-chart');
      const payload = res.data.data || {};
      // Fail closed: only the literal string 'full' opens the door. A
      // missing/malformed/unexpected `mode` (a bug, a stale cache, a
      // tampered response) must never default to showing company-wide
      // data - it must redirect, exactly like a genuine `scoped` caller.
      setMode(payload.mode === 'full' ? 'full' : 'scoped');
      setNodes(payload.nodes || []);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load the org chart';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (mode === 'scoped') {
      navigate('/org-chart', { replace: true });
    }
  }, [mode, navigate]);

  // `orgchart-export.css` is bundled app-wide, so its print rules cannot be
  // scoped to "this page" by nesting them under a class on an element
  // inside the page - by the time a print rule targets `html`/`body` (the
  // background reset), there is no ancestor left to scope through. This
  // class is the scope instead: added only while this component is
  // mounted, removed on unmount, so printing any OTHER route is untouched.
  useEffect(() => {
    document.body.classList.add('org-export-printing');
    return () => {
      document.body.classList.remove('org-export-printing');
    };
  }, []);

  const report = useMemo(() => buildOrgReport(nodes), [nodes]);

  if (loading) {
    return (
      <div data-testid="org-export-page" className="org-export-page">
        <div className="flex justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="org-export-page" className="org-export-page">
        <div className="mx-auto max-w-md py-24 text-center">
          <p className="mb-4 text-danger-600">{error}</p>
          <button
            type="button"
            onClick={fetchData}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // The redirect effect above is already navigating a scoped caller away;
  // render nothing so no company-wide data ever paints, even for a frame.
  if (mode !== 'full' || !report.root) {
    return <div data-testid="org-export-page" className="org-export-page" />;
  }

  const { stats, root, sections } = report;
  // The mini chart only ever draws the connected tree rooted at `root` - an
  // extra top-level root from the multi-root edge case has no place to hang
  // off visually, but it still gets its full section further down the page.
  const chartLeaders = sections.filter((section) => section.leader.manager_id === root.id);

  return (
    <div data-testid="org-export-page" className="org-export-page">
      <div className="org-export-actionbar no-print">
        <button
          type="button"
          data-testid="org-export-download"
          onClick={() => window.print()}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Download PDF
        </button>
        <Link
          to="/org-chart"
          data-testid="org-export-back"
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to chart
        </Link>
      </div>

      <div className="org-export-document">
        {/* A genuine table, not a styling flourish: `<thead>`/`<tfoot>` are
            what let Chromium repeat the header/footer on every printed page
            AND reserve real space for them while doing it - see the long
            comment on `.org-export-print-table` in orgchart-export.css for
            why `position: fixed` looked right but wasn't. Screen mode uses
            the exact same markup; nothing here needs a separate DOM shape. */}
        <table className="org-export-print-table" role="presentation" data-testid="org-export-print-table">
          <thead>
            <tr>
              <td>
                <header className="org-export-header">
                  <div className="org-export-header__brand">
                    {/* logo.svg bakes the wordmark AND a "Building the future." tagline
                        into one static graphic (traced letterforms, not live text).
                        The frame below crops the image down to just the wordmark so
                        the configurable `company.tagline` text underneath - the one
                        `REACT_APP_COMPANY_TAGLINE` actually controls - is the only
                        tagline that ever appears, rather than a second, un-editable
                        copy bleeding out from the image. */}
                    <div className="org-export-header__logo-frame">
                      <img src={logo} alt={`${company.name} logo`} className="org-export-header__logo" />
                    </div>
                    <p className="org-export-header__tagline">{company.tagline}</p>
                  </div>
                  <div className="org-export-header__contact">
                    <p><Mail size={13} /> {company.email}</p>
                    <p><MapPin size={13} /> {company.address}</p>
                  </div>
                </header>
              </td>
            </tr>
          </thead>

          <tfoot>
            <tr>
              <td>
                <footer className="org-export-footer">
                  <p><Phone size={13} /> {company.phone}</p>
                  <p><Globe size={13} /> {company.website}</p>
                  <p><Linkedin size={13} /> {company.linkedin}</p>
                </footer>
              </td>
            </tr>
          </tfoot>

          <tbody>
            <tr>
              <td>
                <main className="org-export-body">
                  <h1 className="org-export-title">Organization Structure</h1>
                  <p className="org-export-subtitle">
                    Employee Reference Document &middot; Confidential &middot; {formatGeneratedAt(report.generatedAt)}
                  </p>

                  <div className="org-export-band">Organizational Chart</div>

                  {/* Connected tree (root -> leaders -> role leaf boxes), CSS pseudo-
                      element connectors drawn by the `.export-tree` rules in
                      orgchart-export.css - an export/print-scoped copy of the
                      interactive chart's nested <ul>/<li> connector technique
                      (see OrgChartTree.js / orgchart.css), so the interactive
                      chart's own CSS/JS is untouched. Root + leaders stay boxed
                      NAMED nodes; each leader's roll-up renders as a vertical
                      stack of boxed "Designation xN" leaf nodes underneath it,
                      one connector segment per stacked item, with a department
                      caption row (Task 7) where a group's department differs
                      from the leader's - unchanged content, only the shape. */}
                  <div className="org-export-chart export-tree" data-testid="org-export-chart">
                    <ul className="export-tree__list export-tree__list--root">
                      <li className="export-tree__root-item">
                        <div className="export-tree__node export-tree__node--root">
                          <MiniPerson person={root} size="lg" />
                        </div>
                        <ul className="export-tree__list export-tree__list--leaders">
                          {chartLeaders.map((section) => {
                            // Task 8: the leader's FULL report list is used (no
                            // person-count cap) - aggregation already collapses
                            // however many people share a title, so a leader
                            // with 7 Software Engineers renders "Software
                            // Engineer x7", not seven names or a "+N more"
                            // person fold. Only the distinct-designation count
                            // is capped, via `buildLeaderRoleGroups`.
                            const reports = section.leader.children || [];
                            const leaderDeptName = normalizedDeptName(section.leader.department_name);
                            const { groups: roleGroups, extraRoles } = buildLeaderRoleGroups(reports);
                            return (
                              <li
                                className="export-tree__leader-item"
                                key={section.leader.id}
                                data-testid={`org-export-chart-leader-${section.leader.id}`}
                              >
                                <div className="export-tree__node export-tree__node--leader">
                                  <MiniPerson person={section.leader} />
                                </div>
                                {reports.length > 0 && (
                                  <ul className="export-tree__stack">
                                    {roleGroups.map((group) => (
                                      <React.Fragment key={group.key}>
                                        {group.department_name !== leaderDeptName && (
                                          <li className="export-tree__stack-caption-row">
                                            <DeptCaption leaderId={section.leader.id} group={group} />
                                          </li>
                                        )}
                                        {group.roles.map((role) => (
                                          <li className="export-tree__stack-item" key={role.position}>
                                            <div className="export-tree__node export-tree__node--role">
                                              <RoleCountLine
                                                leaderId={section.leader.id}
                                                deptKey={group.key}
                                                role={role}
                                              />
                                            </div>
                                          </li>
                                        ))}
                                      </React.Fragment>
                                    ))}
                                    {extraRoles > 0 && (
                                      <li className="export-tree__stack-item export-tree__stack-item--more">
                                        <p className="export-chart__more">+{extraRoles} more roles</p>
                                      </li>
                                    )}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    </ul>
                  </div>

                  <div className="org-export-stats">
                    <div className="stat-tile">
                      <span className="stat-tile__value">{stats.headcount}</span>
                      <span className="stat-tile__label">Total Headcount</span>
                    </div>
                    <div className="stat-tile">
                      <span className="stat-tile__value">{stats.departments}</span>
                      <span className="stat-tile__label">Departments</span>
                    </div>
                    <div className="stat-tile">
                      <span className="stat-tile__value">
                        {stats.largestDepartment ? stats.largestDepartment.count : 0}
                      </span>
                      <span className="stat-tile__label">
                        Largest Dept ({stats.largestDepartment ? stats.largestDepartment.name : '-'})
                      </span>
                    </div>
                    <div className="stat-tile">
                      <span className="stat-tile__value">{stats.rootDirectReports}</span>
                      <span className="stat-tile__label">Direct Reports to {root.position}</span>
                    </div>
                  </div>

                  <p className="org-export-intro">
                    This document outlines the organizational structure of the company, including all departments,
                    reporting lines, team leads, and role distributions. It is intended to help all employees
                    understand how the organization is structured and where each function sits.
                  </p>

                  <section className="org-export-section" data-testid="org-export-section-root">
                    <div className="org-export-band">Leadership</div>
                    <div className="org-export-leader-row">
                      <span className="org-export-leader-row__name">{root.first_name} {root.last_name}</span>
                      <span className="org-export-leader-row__position">{root.position}</span>
                    </div>
                  </section>

                  {sections.map((section) => (
                    <section
                      className="org-export-section"
                      data-testid={`org-export-section-${section.leader.id}`}
                      key={section.leader.id}
                    >
                      <div className="org-export-band">
                        {section.leader.department_name || section.leader.position}
                      </div>
                      <div className="org-export-leader-row">
                        <span className="org-export-leader-row__name">
                          {section.leader.first_name} {section.leader.last_name}
                        </span>
                        <span className="org-export-leader-row__meta">
                          <span className="org-export-leader-row__position">{section.leader.position}</span>
                          <strong className="org-export-leader-row__headcount">
                            Reporting line: {section.headcount}
                          </strong>
                        </span>
                      </div>

                      {/* Task 10 (reference-document style): a direct report
                          WITH descendants of their own stays named - left
                          cell is their name + title, right is their WHOLE
                          subtree rolled up into a "Designation xN" chain. A
                          direct report who is a LEAF is never named - every
                          leaf direct report is grouped by designation into
                          its own role row instead (left = designation,
                          right = the count). */}
                      <table className="org-export-stream-table">
                        <tbody>
                          {(section.managerRows.length > 0 || section.roleRows.length > 0) ? (
                            <>
                              {section.managerRows.map((row) => (
                                <tr key={row.manager.id} className="org-export-stream-row">
                                  <td className="org-export-stream-row__lead">
                                    <span className="org-export-stream-row__lead-name">
                                      {row.manager.first_name} {row.manager.last_name}
                                    </span>
                                    <span className="org-export-stream-row__lead-position">
                                      {row.manager.position}
                                    </span>
                                  </td>
                                  <td className="org-export-stream-row__roles">
                                    {formatRoleCounts(row.roles)}
                                  </td>
                                </tr>
                              ))}
                              {section.roleRows.map((role) => (
                                <tr key={role.position} className="org-export-stream-row">
                                  <td className="org-export-stream-row__lead">
                                    <span className="org-export-stream-row__lead-name">{role.position}</span>
                                  </td>
                                  <td className="org-export-stream-row__roles">×{role.count}</td>
                                </tr>
                              ))}
                            </>
                          ) : (
                            <tr className="org-export-stream-row">
                              <td className="org-export-stream-row__roles" colSpan={2}>
                                {formatRoleCounts(section.ownRoleCounts)}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </section>
                  ))}

                  <p className="org-export-closing">
                    This document reflects the current headcount and reporting structure as of the counts shown,
                    which reflect active employees only. For queries, please contact Human Resources.
                    {stats.unassigned > 0 && (
                      <span data-testid="org-export-unassigned-note">
                        {' '}{formatUnassignedNote(stats.unassigned)}
                      </span>
                    )}
                  </p>
                </main>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrgChartExport;
